/** Version payload returned by the add-in server. */
export interface VersionResponse {
  /** Build identity currently served by the deployment. */
  currentVersion: string;
  /** Oldest client protocol accepted by the deployment. */
  minimumSupportedVersion: number;
}

/** Dependencies and timing controls for the host-neutral update controller. */
export interface ClientUpdateControllerDependencies {
  /** Build identity embedded in the running frontend bundle. */
  currentBuildVersion: string;
  /** Fetches current deployment version state. */
  fetchVersion: () => Promise<VersionResponse>;
  /** Reports whether the host has work that must not be interrupted. */
  isBusy: () => boolean;
  /** Persists host state immediately before a reload. */
  beforeReload: () => Promise<void> | void;
  /** Reloads the taskpane for the supplied deployment version. */
  reload: (version: string) => void;
  /** Receives update-pending state changes for submission gating. */
  onUpdatePending?: (pending: boolean) => void;
  /** Browser focus event source. */
  windowTarget?: Pick<Window, "addEventListener" | "removeEventListener">;
  /** Browser visibility event source and state. */
  documentTarget?: Pick<Document, "addEventListener" | "removeEventListener" | "visibilityState">;
  /** Random source used only to spread simultaneous reloads. */
  random?: () => number;
  /** Period between healthy version checks. */
  pollIntervalMs?: number;
  /** Initial retry delay after a failed version check. */
  retryBaseMs?: number;
  /** Maximum retry delay after repeated failures. */
  retryMaxMs?: number;
  /** Maximum randomized delay before a safe reload. */
  reloadJitterMaxMs?: number;
}

/** Lifecycle surface exposed to Excel and PowerPoint integrations. */
export interface ClientUpdateController {
  /** Starts startup, interval, focus, and visibility checks. */
  start(): void;
  /** Stops timers and browser lifecycle listeners. */
  stop(): void;
  /** Performs one version check and resolves after its result is handled. */
  checkNow(): Promise<void>;
  /** Re-evaluates a deferred reload after host busy state changes. */
  notifyStateChanged(): void;
  /** Marks a server-required update pending and begins safe reload handling. */
  handleUpgradeRequired(version?: string): Promise<void>;
  /** Reports whether submissions must remain blocked for an update. */
  isUpdatePending(): boolean;
}

const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000;
const DEFAULT_RETRY_BASE_MS = 15_000;
const DEFAULT_RETRY_MAX_MS = 5 * 60 * 1000;
const DEFAULT_RELOAD_JITTER_MAX_MS = 5_000;

/**
 * Creates the shared update lifecycle used by both Office hosts.
 *
 * @param dependencies - Host boundaries and timing controls.
 * @returns A controller that remains dormant until `start` or `checkNow` is called.
 * @throws Never from version-check failures; those failures schedule bounded retries.
 * @sideEffects Once started, registers lifecycle listeners and timers; a confirmed
 * stale build can persist host state and invoke the supplied reload callback.
 */
export function createClientUpdateController(
  dependencies: ClientUpdateControllerDependencies
): ClientUpdateController {
  const pollIntervalMs = dependencies.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const retryBaseMs = dependencies.retryBaseMs ?? DEFAULT_RETRY_BASE_MS;
  const retryMaxMs = dependencies.retryMaxMs ?? DEFAULT_RETRY_MAX_MS;
  const reloadJitterMaxMs =
    dependencies.reloadJitterMaxMs ?? DEFAULT_RELOAD_JITTER_MAX_MS;
  const random = dependencies.random ?? Math.random;

  let started = false;
  let stopped = false;
  let updatePending = false;
  let targetVersion: string | null = null;
  let reloadScheduled = false;
  let reloadStarted = false;
  let failures = 0;
  let inFlight: Promise<void> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let reloadTimer: ReturnType<typeof setTimeout> | null = null;

  const clearRetry = () => {
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const scheduleRetry = () => {
    if (stopped || retryTimer !== null || updatePending) return;
    const exponent = Math.max(0, failures - 1);
    const delay = Math.min(retryBaseMs * 2 ** exponent, retryMaxMs);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      void checkNow();
    }, delay);
  };

  const requestReloadIfIdle = () => {
    if (
      stopped ||
      !updatePending ||
      reloadScheduled ||
      reloadStarted ||
      dependencies.isBusy()
    ) {
      return;
    }

    reloadScheduled = true;
    const jitter = Math.max(0, random()) * reloadJitterMaxMs;
    reloadTimer = setTimeout(async () => {
      reloadTimer = null;
      reloadScheduled = false;
      if (stopped || reloadStarted || dependencies.isBusy() || !targetVersion) return;

      reloadStarted = true;
      try {
        await dependencies.beforeReload();
        if (!stopped) dependencies.reload(targetVersion);
      } catch {
        reloadStarted = false;
        if (!stopped) {
          reloadScheduled = true;
          reloadTimer = setTimeout(() => {
            reloadTimer = null;
            reloadScheduled = false;
            requestReloadIfIdle();
          }, retryBaseMs);
        }
      }
    }, jitter);
  };

  const markPending = (version: string) => {
    if (!updatePending) {
      updatePending = true;
      dependencies.onUpdatePending?.(true);
    }
    targetVersion = version;
    clearRetry();
    requestReloadIfIdle();
  };

  const performCheck = async () => {
    try {
      const response = await dependencies.fetchVersion();
      if (stopped) return;
      if (
        !response ||
        typeof response.currentVersion !== "string" ||
        !response.currentVersion.trim() ||
        !Number.isSafeInteger(response.minimumSupportedVersion) ||
        response.minimumSupportedVersion < 1
      ) {
        throw new Error("Invalid version response");
      }

      failures = 0;
      clearRetry();
      if (response.currentVersion !== dependencies.currentBuildVersion) {
        markPending(response.currentVersion);
      }
    } catch {
      failures += 1;
      scheduleRetry();
    }
  };

  const checkNow = (): Promise<void> => {
    if (stopped) return Promise.resolve();
    if (!inFlight) {
      inFlight = performCheck().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  };

  const onFocus = () => {
    void checkNow();
  };
  const onVisibilityChange = () => {
    if (dependencies.documentTarget?.visibilityState === "visible") {
      void checkNow();
    }
  };

  return {
    start() {
      if (started) return;
      started = true;
      stopped = false;
      dependencies.windowTarget?.addEventListener("focus", onFocus);
      dependencies.documentTarget?.addEventListener("visibilitychange", onVisibilityChange);
      pollTimer = setInterval(() => void checkNow(), pollIntervalMs);
      void checkNow();
    },
    stop() {
      stopped = true;
      started = false;
      if (pollTimer !== null) clearInterval(pollTimer);
      if (reloadTimer !== null) clearTimeout(reloadTimer);
      pollTimer = null;
      reloadTimer = null;
      reloadScheduled = false;
      clearRetry();
      dependencies.windowTarget?.removeEventListener("focus", onFocus);
      dependencies.documentTarget?.removeEventListener("visibilitychange", onVisibilityChange);
    },
    checkNow,
    notifyStateChanged() {
      requestReloadIfIdle();
    },
    async handleUpgradeRequired(version = "latest") {
      if (version === dependencies.currentBuildVersion) return;
      markPending(version);
    },
    isUpdatePending() {
      return updatePending;
    },
  };
}
